import React, { useState } from 'react';
import { AxiosError } from 'axios'; // Import if using Axios for API calls
import { AdminUserDetailDto, AdminCreateUserDto, AdminUpdateUserDto, AdminReportDto } from '../../types'; // Adjust path as needed
import { ValidationError } from './ValidationError';
import { scrollToFirstErrorField } from '../../utils/scrollToFirstError';
import { parseAxiosValidationErrors } from '../../utils/productValidation';
import { ValidatedInput } from './ValidatedInput';
import AdminModalShell from './AdminModalShell';

interface UserModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedUser: AdminUserDetailDto | null;
    createFormData: AdminCreateUserDto;
    setCreateFormData: React.Dispatch<React.SetStateAction<AdminCreateUserDto>>;
    updateFormData: AdminUpdateUserDto;
    setUpdateFormData: React.Dispatch<React.SetStateAction<AdminUpdateUserDto>>;
    newStrikeReason: string;
    setNewStrikeReason: React.Dispatch<React.SetStateAction<string>>;
    handleSaveUser: () => Promise<void>;
    handleAddStrike: () => Promise<void>;
    handleRemoveStrike: (strikeId: string) => Promise<void>;
    handleBanUser: () => Promise<void>;
    handleUnbanUser: () => Promise<void>;
    handleResolveReport: (reportId: string) => Promise<void>;
    handleSetEmailConfirmed?: (confirmed: boolean) => Promise<void>;
    handleResendEmailConfirmation?: () => Promise<void>;
    isLoading: boolean; // General loading for core user data
    additionalFields: string[]; // Optional: Specific for strikes section if fetched separately
}
const UserModal: React.FC<UserModalProps> = ({
    isOpen,
    onClose,
    isCreating,
    selectedUser,
    createFormData,
    setCreateFormData,
    updateFormData,
    setUpdateFormData,
    newStrikeReason,
    setNewStrikeReason,
    handleSaveUser,
    handleAddStrike,
    handleRemoveStrike,
    handleBanUser,
    handleUnbanUser,
    isLoading,
    additionalFields,
    handleResolveReport,
    handleSetEmailConfirmed,
    handleResendEmailConfirmation,
}) => {
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [emailActionBusy, setEmailActionBusy] = useState(false);
    if (!isOpen) return null;
    const validatePasswordPolicy = (password: string | undefined): string | null => {
        if (!password || !password.trim()) return null;
        if (password.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
        if (!/\d/.test(password)) return 'Password must contain at least one digit';
        if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least one special character';
        return null;
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        const formData = isCreating ? createFormData : updateFormData;
        if (!formData.email?.trim()) newErrors.Email = 'Email is required';
        if (!formData.userName?.trim()) newErrors.UserName = 'Username is required';
        if (!formData.roles || formData.roles.length === 0) newErrors.Roles = 'At least one role is required';

        // Password UX validation (client-side for immediate feedback)
        const pwd = formData.password;
        if (isCreating) {
            if (!pwd?.trim()) {
                newErrors.Password = 'Password is required';
            } else {
                const policyErr = validatePasswordPolicy(pwd);
                if (policyErr) newErrors.Password = policyErr;
            }
        } else if (pwd && pwd.trim()) {
            // On edit, only validate if user entered something (resetting password)
            const policyErr = validatePasswordPolicy(pwd);
            if (policyErr) newErrors.Password = policyErr;
        }
        // If you add an integer field, e.g.: if (formData.someInt < 0) newErrors.SomeInt = 'Cannot be negative';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async () => {
        setErrors({}); // Clear previous errors
        if (!validateForm()) {
            scrollToFirstErrorField();
            return;
        }
        try {
            await handleSaveUser();
        } catch (err: unknown) {
            if (err instanceof AxiosError && err.response) {
                // Type guard for AxiosError
                const backendErrors = parseAxiosValidationErrors(err);
                setErrors(backendErrors);
                scrollToFirstErrorField(backendErrors);
            } else if (err instanceof Error) {
                setErrors({ General: 'Save failed: ' + err.message });
            } else {
                setErrors({ General: 'Save failed: Unknown error' });
            }
        }
    };
    const formatDate = (isoString?: string): string => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true // AM/PM
        });
    };
    // Helper: Determine if save should be disabled (adapt for other modals)
    const isSaveDisabled = isLoading || (!isCreating && selectedUser === null);
    if (!isOpen) return null;
    return (
        <AdminModalShell
            title={isCreating ? 'Create User' : 'Edit User'}
            onClose={onClose}
            size="md"
        >
            {/* Form Fields - Disable specific ones if loading (e.g., only those relying on fetched data) */}
            <ValidationError error={errors.General} />
            <div className="grid grid-cols-1 gap-4">
                <div data-error-field="Email">
                    {/*<label className="block text-sm font-medium text-gray-700 mb-1">Email</label>*/}
                    <ValidatedInput
                        label="Email"
                        name="email"
                        value={isCreating ? createFormData.email : updateFormData.email ?? ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (isCreating) setCreateFormData({ ...createFormData, email: value });
                            else setUpdateFormData({ ...updateFormData, email: value });
                            setErrors(prev => {
                                const n = { ...prev };
                                delete n.Email;
                                return n;
                            });
                        }}
                        disabled={!isCreating && isLoading && additionalFields.includes('email')}
                        error={errors.Email}
                    />
                </div>
                <div data-error-field="UserName">
                    <ValidatedInput
                        label="Username"
                        name="username"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        placeholder="Username"
                        value={isCreating ? createFormData.userName : updateFormData.userName ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            if (isCreating) setCreateFormData({ ...createFormData, userName: value });
                            else setUpdateFormData({ ...updateFormData, userName: value });
                            setErrors(prev => {
                                const n = { ...prev };
                                delete n.UserName;
                                return n;
                            });
                        }}
                        disabled={!isCreating && isLoading && additionalFields.includes('userName')}
                        error={errors.UserName}
                    />
                </div>
                <div data-error-field="Password">
                    <ValidatedInput
                        label={isCreating ? "Password" : "New Password (optional)"}
                        name="password"
                        type="password"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        placeholder={isCreating ? "Password" : "Leave blank to keep current password"}
                        value={isCreating ? createFormData.password : updateFormData.password ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            if (isCreating) setCreateFormData({ ...createFormData, password: value });
                            else setUpdateFormData({ ...updateFormData, password: value });
                            // Clear password error on typing (UX feedback, consistent with other modals like ProductModal)
                            setErrors(prev => {
                                const n = { ...prev };
                                delete n.Password;
                                return n;
                            });
                        }}
                        error={errors.Password}
                    />
                    {!isCreating && (
                        <p className="text-xs text-gray-500 mt-1">Enter a new password to reset it. Must meet site password requirements (length, upper/lower/digit/special).</p>
                    )}
                </div>
                <div data-error-field="Roles">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Roles</label>
                    <div className="flex flex-col gap-2 p-3 border border-gray-300 rounded-md bg-white">
                        {['User', 'Admin'].map(role => {
                            const currentRoles = isCreating ? createFormData.roles : (updateFormData.roles ?? []);
                            const isChecked = currentRoles.includes(role);
                            const handleChange = (checked: boolean) => {
                                let newRoles = [...currentRoles];
                                if (checked) {
                                    if (!newRoles.includes(role)) newRoles.push(role);
                                } else {
                                    newRoles = newRoles.filter(r => r !== role);
                                }

                                // Keep the legacy isAdmin field in sync with whether "Admin" role is selected
                                const newIsAdmin = newRoles.includes('Admin');

                                if (isCreating) {
                                    setCreateFormData({ ...createFormData, roles: newRoles, isAdmin: newIsAdmin });
                                } else {
                                    setUpdateFormData({ ...updateFormData, roles: newRoles, isAdmin: newIsAdmin });
                                }

                                // Clear roles error as soon as user interacts (UX)
                                setErrors(prev => {
                                    const n = { ...prev };
                                    delete n.Roles;
                                    return n;
                                });
                            };
                            return (
                                <label key={role} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => handleChange(e.target.checked)}
                                        disabled={!isCreating && isLoading && additionalFields.includes('roles')}
                                        className="w-4 h-4 accent-blue-600"
                                    />
                                    <span className="text-sm text-gray-800">{role}</span>
                                    {!isCreating && isChecked && (
                                        <span className="text-xs text-green-600">(currently assigned)</span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Check the roles this user should have.</p>
                    <ValidationError error={errors.Roles} />
                </div>
                {/* New: Lockout Enabled */}
                <div className="flex items-center">
                    <label className="block text-sm font-medium text-gray-700 mr-2">Lockout Enabled</label>
                    <input
                        type="checkbox"
                        checked={isCreating ? (createFormData.lockoutEnabled ?? false) : (updateFormData.lockoutEnabled ?? selectedUser?.lockoutEnabled ?? false)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.checked;
                            if (isCreating) setCreateFormData({ ...createFormData, lockoutEnabled: value });
                            else setUpdateFormData({ ...updateFormData, lockoutEnabled: value });
                        }}
                        disabled={!isCreating && isLoading && additionalFields.includes('lockoutEnabled')}
                        className={`${!isCreating && isLoading && additionalFields.includes('lockoutEnabled') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {!isCreating && isLoading && additionalFields.includes('lockoutEnabled') && (
                        <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                    )}
                    <ValidationError error={errors.LockoutEnabled} />
                </div>
                {/* New: Two Factor Enabled */}
                <div className="flex items-center">
                    <label className="block text-sm font-medium text-gray-700 mr-2">Two-Factor Enabled</label>
                    <input
                        type="checkbox"
                        checked={isCreating ? (createFormData.twoFactorEnabled ?? false) : (updateFormData.twoFactorEnabled ?? selectedUser?.twoFactorEnabled ?? false)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.checked;
                            if (isCreating) setCreateFormData({ ...createFormData, twoFactorEnabled: value });
                            else setUpdateFormData({ ...updateFormData, twoFactorEnabled: value });
                        }}
                        disabled={!isCreating && isLoading && additionalFields.includes('twoFactorEnabled')}
                        className={`${!isCreating && isLoading && additionalFields.includes('twoFactorEnabled') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {!isCreating && isLoading && additionalFields.includes('twoFactorEnabled') && (
                        <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                    )}
                    <ValidationError error={errors.TwoFactorEnabled} />
                </div>
                {/* New: Lockout End (editable in update as ISO string) */}
                {!isCreating && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Lockout End Date (ISO)</label>
                        <div className="relative">
                            <input
                                type="text"
                                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('lockoutEndDate')
                                        ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                        : ''
                                    }`}
                                placeholder="YYYY-MM-DDTHH:MM:SSZ"
                                value={updateFormData.lockoutEnd ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = e.target.value.trim();
                                    setUpdateFormData({ ...updateFormData, lockoutEnd: value === '' ? undefined : value });
                                }}
                                disabled={!isCreating && isLoading && additionalFields.includes('lockoutEndDate')}
                            />
                            {!isCreating && isLoading && additionalFields.includes('lockoutEndDate') && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                                </div>
                            )}
                        </div>
                        <ValidationError error={errors.LockoutEnd} />
                    </div>
                )}
                {/* New: Phone Number */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phonenumber</label>
                    <div className="relative">
                        <input
                            name="phonenumber"
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('phoneNumber')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            placeholder="Phone Number"
                            value={isCreating ? createFormData.phoneNumber ?? '' : updateFormData.phoneNumber ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (isCreating) setCreateFormData({ ...createFormData, phoneNumber: value });
                                else setUpdateFormData({ ...updateFormData, phoneNumber: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('phoneNumber')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('phoneNumber') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.PhoneNumber} />
                </div>
                {/* New: Address Fields */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <div className="relative">
                        <input
                            name="streetaddress"
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('streetAddress')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            placeholder="Street Address"
                            value={isCreating ? createFormData.streetAddress ?? '' : updateFormData.streetAddress ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (isCreating) setCreateFormData({ ...createFormData, streetAddress: value });
                                else setUpdateFormData({ ...updateFormData, streetAddress: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('streetAddress')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('streetAddress') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.StreetAddress} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <div className="relative">
                        <input
                            name="city"
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('city')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            placeholder="City"
                            value={isCreating ? createFormData.city ?? '' : updateFormData.city ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (isCreating) setCreateFormData({ ...createFormData, city: value });
                                else setUpdateFormData({ ...updateFormData, city: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('city')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('city') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.City} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                    <div className="relative">
                        <input
                            name="postalCode"
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('postalCode')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            placeholder="Postal Code"
                            value={isCreating ? createFormData.postalCode ?? '' : updateFormData.postalCode ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (isCreating) setCreateFormData({ ...createFormData, postalCode: value });
                                else setUpdateFormData({ ...updateFormData, postalCode: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('postalCode')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('postalCode') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.PostalCode} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <div className="relative">
                        <input
                            name="country"
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('country')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            placeholder="Country"
                            value={isCreating ? createFormData.country ?? '' : updateFormData.country ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (isCreating) setCreateFormData({ ...createFormData, country: value });
                                else setUpdateFormData({ ...updateFormData, country: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('country')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('country') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.Country} />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Id - for DAC7 - required if flagged</label>
                <div className="relative">
                    <input
                        name="taxid"
                        className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('taxId')
                                ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                : ''
                            }`}
                        placeholder="Tax ID"
                        value={isCreating ? '' : updateFormData.taxId ?? selectedUser?.taxId ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            setUpdateFormData({ ...updateFormData, taxId: value });
                        }}
                        disabled={!isCreating && isLoading && additionalFields.includes('taxId')}
                    />
                    {!isCreating && isLoading && additionalFields.includes('taxId') && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                            <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        </div>
                    )}
                </div>
                <ValidationError error={errors.TaxId} />
            </div>
            {!isCreating && selectedUser && (
                <>
                    {/* New: Additional Details (read-only) */}
                    <h3 className="mt-6 text-lg font-bold text-gray-800">Additional Details</h3>
                    {!isLoading ? (
                        <>
                            <p>Created: {formatDate(selectedUser.createdDate)}</p>
                            <p>Lockout End: {formatDate(selectedUser.lockoutEndDate)}</p>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={!!selectedUser.emailConfirmed}
                                        disabled={emailActionBusy || isLoading || !handleSetEmailConfirmed}
                                        onChange={async (e) => {
                                            if (!handleSetEmailConfirmed) return;
                                            const next = e.target.checked;
                                            setEmailActionBusy(true);
                                            try {
                                                await handleSetEmailConfirmed(next);
                                            } catch (err) {
                                                setErrors({
                                                    General:
                                                        err instanceof Error
                                                            ? err.message
                                                            : 'Failed to update email confirmation',
                                                });
                                            } finally {
                                                setEmailActionBusy(false);
                                            }
                                        }}
                                    />
                                    Email verified (shop login allowed when checked)
                                </label>
                                <p className="text-xs text-gray-500">
                                    Reflects the user&apos;s current Identity EmailConfirmed value (not a form default).
                                    New shop users start unverified. Check only as a support bypass so they can log in
                                    without the confirmation email; uncheck to force verification again.
                                </p>
                                <div className="flex flex-wrap gap-2 pt-1">
                                    {handleSetEmailConfirmed && !selectedUser.emailConfirmed && (
                                        <button
                                            type="button"
                                            disabled={emailActionBusy}
                                            className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                                            onClick={async () => {
                                                setEmailActionBusy(true);
                                                try {
                                                    await handleSetEmailConfirmed(true);
                                                } finally {
                                                    setEmailActionBusy(false);
                                                }
                                            }}
                                        >
                                            Force confirm email
                                        </button>
                                    )}
                                    {handleResendEmailConfirmation && !selectedUser.emailConfirmed && (
                                        <button
                                            type="button"
                                            disabled={emailActionBusy}
                                            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                            onClick={async () => {
                                                setEmailActionBusy(true);
                                                try {
                                                    await handleResendEmailConfirmation();
                                                } finally {
                                                    setEmailActionBusy(false);
                                                }
                                            }}
                                        >
                                            Resend confirmation email
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p>Phone: {selectedUser.phoneNumber || 'None'}</p>
                            <p>Address: {selectedUser.streetAddress || ''}, {selectedUser.city || ''} {selectedUser.postalCode || ''}, {selectedUser.country || 'None'}</p>
                            <p>Tax ID: {selectedUser.taxId || 'None'}</p>
                            <p>Tax Info Provided: {selectedUser.hasProvidedTaxInfo ? 'Yes' : 'No'}</p>
                            <p>Flagged for DAC7: {selectedUser.isFlagged ? 'Yes' : 'No'}</p>
                        </>
                    ) : (
                        <div className="flex items-center justify-center py-4">
                            <div className="h-6 w-6 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600 mr-2"></div>
                            <p className="text-gray-600">Loading additional details...</p>
                        </div>
                    )}
                    {/* Reports Section */}
                    {!isCreating && selectedUser && (
                        <>
                            {/* Reports Section - Actionable */}
                            <h3 className="text-lg font-semibold mt-8 mb-3">Reports against this user</h3>

                            {!isLoading && additionalFields.includes('reports') ? (
                                !selectedUser?.reports || selectedUser.reports.length === 0 ? (
                                    <p className="text-gray-500 py-2">No reports yet.</p>
                                ) : (
                                    <div className="space-y-3 max-h-80 overflow-y-auto">
                                        {selectedUser.reports.map((report: AdminReportDto) => (
                                            <div key={report.id} className="bg-red-50 border border-red-200 rounded-xl p-4">
                                                <div className="flex justify-between items-start">
                                                    <span className="font-medium">{report.reason}</span>
                                                    <span className="text-xs text-gray-500">
                                                        {new Date(report.created).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {report.description && <p className="text-sm mt-2">{report.description}</p>}

                                                <div className="flex items-center justify-between mt-4">
                                                    <span className="text-xs px-3 py-1 rounded-full bg-red-100 text-red-700">
                                                        {report.status}
                                                    </span>

                                                    {report.status === 'Pending' && (
                                                        <button
                                                            onClick={() => handleResolveReport(report.id)}
                                                            className="text-xs px-4 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                                        >
                                                            Mark as Reviewed
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            ) : isLoading && additionalFields.includes('reports') ? (
                                <div className="flex items-center justify-center py-4">
                                    <div className="h-6 w-6 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600 mr-2"></div>
                                    <p className="text-gray-600">Loading reports...</p>
                                </div>
                            ) : (
                                <p className="text-gray-500 py-2">No reports yet.</p>
                            )}
                        </>
                    )}
                    {/* Strikes Section */}
                    <h3 className="mt-6 text-lg font-bold text-gray-800">Strikes</h3>
                    {!isLoading && additionalFields.includes('strikes') ? (
                        !selectedUser?.strikes || selectedUser.strikes.length === 0 ? (
                            <p className="text-gray-500 py-2">No strikes yet.</p>
                        ) : (
                            <div className="overflow-x-auto mt-2 rounded-lg shadow">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {selectedUser.strikes.map((strike) => (
                                            <tr key={strike.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{strike.reason}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDate(strike.created)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    <button onClick={() => handleRemoveStrike(strike.id ?? '')} className="text-red-600 hover:text-red-900 font-medium">Remove</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : isLoading && additionalFields.includes('strikes') ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="h-6 w-6 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600 mr-2"></div>
                            <p className="text-gray-600">Loading strikes...</p>
                        </div>
                    ) : (
                        <p className="text-gray-500 py-2">No strikes yet.</p>
                    )}
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Strike Reason</label>
                        <textarea
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                            placeholder="New Strike Reason"
                            value={newStrikeReason}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewStrikeReason(e.target.value)}
                        />
                    </div>
                    <button onClick={handleAddStrike} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium">Add Strike</button>
                    {/* Status and Ban/Unban */}
                    <h3 className="mt-6 text-lg font-bold text-gray-800">Status: {selectedUser.status}</h3>
                    <div className="mt-2 flex space-x-3">
                        <button onClick={handleBanUser} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium">Ban User</button>
                        <button onClick={handleUnbanUser} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium">Unban User</button>
                    </div>
                </>
            )}
            <div className="mt-6 flex justify-end">
                <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium mr-3"
                    disabled={isSaveDisabled}>Save</button>
                <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors font-medium">Cancel</button>
            </div>
        </AdminModalShell>
    );
};
export default UserModal;